import { makeAutoObservable, runInAction } from "mobx";
import React, { useRef, useEffect, useLayoutEffect } from "react";
import { observer } from "mobx-react-lite";


const storesByName: {[key: string]: any} = {};
const storeRefCount: {[key: string]: number} = {};


export function noop(): any {}

export function container<PROPS>(
    BaseComponent: (props: PROPS) => any,
    StoreClass: any
): (props: Omit<PROPS, "store"> & {store?: any, name?: any}) => any {
    const ObserverComponent = React.memo(observer(BaseComponent)) as any;
    const setterCache = new Map<string, string | false>();

    function getOrCacheSetter(store: any, key: string): string | false {
        if (!setterCache.has(key)) {
            const setterName = `set${key[0].toUpperCase()}${key.slice(1)}`;
            setterCache.set(key, store.hasOwnProperty(setterName) ? setterName : false);
        }
        return setterCache.get(key)!;
    }

    let component = function (props: any) {
        const storeRef = useRef(props.store as any);
        const nameRef = useRef(props.name);

        function updateProps(reason: string) {
            const store = storeRef.current;
            const storeName = StoreClass.name + (props.name != null ? `[${props.name}]` : '');
            const changes: Record<string, {from: any, to: any}> = {};
            for (let key of Object.keys(props)) {
                if (key === "children") continue;
                if (store[key] !== props[key]) {
                    changes[key] = { from: store[key], to: props[key] };
                }
            }
            console.log(`[mobx container] updateProps`, {
                store: storeName,
                reason,
                changes,
                allProps: props,
                stack: new Error().stack,
            });
            runInAction(() => {
                for (let key of Object.keys(props)) {
                    if (key == "children") {
                        continue;
                    }
                    if (store[key] != props[key]) {
                        const setter = getOrCacheSetter(store, key);
                        if (setter) {
                            store[setter](props[key]);
                        } else {
                            store[key] = props[key];
                        }
                    }
                }
            });
        }

        const isMounted = useRef(false);

        if (!storeRef.current) {
            if (props.name !== undefined && storesByName[props.name] !== undefined) {
                storeRef.current = storesByName[props.name];
                storeRefCount[props.name] = (storeRefCount[props.name] || 0) + 1;
            } else {
                storeRef.current = new StoreClass(props);
                if (props.name !== undefined) {
                    storesByName[props.name] = storeRef.current;
                    storeRefCount[props.name] = 1;
                }
            }
            updateProps('initial mount');
        }

        useLayoutEffect(() => {
            if (!isMounted.current) {
                isMounted.current = true;
                return;
            }
            updateProps('layout effect (re-render)');
        });

        useEffect(() => {
            return () => {
                const store = storeRef.current;
                const name = nameRef.current;
                if (name !== undefined) {
                    storeRefCount[name]--;
                    if (storeRefCount[name] <= 0) {
                        delete storeRefCount[name];
                        delete storesByName[name];
                        store?.dispose?.();
                    }
                } else {
                    store?.dispose?.();
                }
            };
        }, []);

        return <ObserverComponent {...props} store={storeRef.current} children={props.children}/>;
    } as any;
    component.displayName = (StoreClass as any).name + 'Container';
    return component;
}

export function initComponentStore(store: any, overrides?: {[key: string]: any}) {
    overrides = overrides || {};

    for (let key of Object.keys(store)) {
        if (store[key] === noop) {
            overrides[key] = false;
        }
    }
    makeAutoObservable(store, overrides, {autoBind: true});
}
